/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from 'styled-components';
import React, { useEffect, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Table,
} from 'reactstrap';
import { studentActions, classActions, attendanceActions, attendanceStudentRecordActions, scheduleActions, tuitionDetailActions } from '../../redux/actions';
import { StyledTable, TuitionContainer, SuccessButton } from './TuitionManage.styled';
import { studentService } from '../../services/student.service';
import classMonthlySessionService from '../../services/classMonthlySession.service';
import tuitionDetailService from '../../services/tuitionDetail.service';
import bankAccountService, { BankAccount } from '../../services/bankAccount.service';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

interface FeeResult {
  absenceDates: any;
  presenceDates: any;
  student: any; // Thay `any` bằng kiểu chính xác nếu có
  absencesLastMonth: number;
  presencesLastMonth: number;
  feePerSession: number;
  currentMonthFee: number;
  lastMonthAbsenceFee: number;
  lastMonthPresenceFee: number;
  totalFeeWithPreviousMonthFee: number;
  totalFeeMinusPreviousAbsenceFee: number;
}

type Schedule = {
  id: number;
  classId: number;
  dayOfWeek: number; // 2 -> Thứ Hai, ... 8 -> Chủ Nhật
  startTime: string;
  endTime: string;
  createdAt: string;
  updatedAt: string;
};

const TuitionManageNew: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [attendanceFee, setAttendanceFee] = useState<FeeResult[]>([]);

  // States for left panel student list
  const [searchText, setSearchText] = useState('');
  const [filterClassId, setFilterClassId] = useState('');

  // Confirmed sessions từ DB: classId → mảng sessionDate (YYYY-MM-DD) — chỉ isActive=true
  const [confirmedSessionsMap, setConfirmedSessionsMap] = useState<Record<number, string[]>>({});
  // Holiday sessions: classId → mảng ngày nghỉ lễ của lớp (isActive=false)
  const [holidaySessionsMap, setHolidaySessionsMap] = useState<Record<number, string[]>>({});
  const [confirmedSessionsLoaded, setConfirmedSessionsLoaded] = useState(false);

  // ── Multi-month fee summary ──
  const _now = new Date();
  const _cm = _now.getMonth() + 1;
  const _cy = _now.getFullYear();
  const [summaryFromMonth, setSummaryFromMonth] = useState(_cm === 1 ? 12 : _cm - 1);
  const [summaryFromYear, setSummaryFromYear] = useState(_cm === 1 ? _cy - 1 : _cy);
  const [summaryToMonth, setSummaryToMonth] = useState(_cm);
  const [summaryToYear, setSummaryToYear] = useState(_cy);
  const [multiMonthData, setMultiMonthData] = useState<Record<number, any[]>>({});
  const [multiMonthLoading, setMultiMonthLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyBankSuccess, setCopyBankSuccess] = useState<'top' | 'bottom' | null>(null);

  // Bank accounts
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  // STK picker modal (when selected students use different bank accounts)
  const [stkModalOpen, setStkModalOpen] = useState(false);
  const [stkModalPosition, setStkModalPosition] = useState<'top' | 'bottom'>('top');
  const [stkModalMonths, setStkModalMonths] = useState<number[] | undefined>(undefined);
  const [stkModalChosenId, setStkModalChosenId] = useState<number | ''>('');


  // Individual student settings
  const [studentExtraFee, setStudentExtraFee] = useState<Record<string, boolean>>({}); // {studentId: true/false}
  const [studentIncludePrevMonth, setStudentIncludePrevMonth] = useState<Record<string, boolean>>({}); // {studentId: true/false}

  // Individual student editable data
  const [studentExtraFeeAmount, setStudentExtraFeeAmount] = useState<Record<string, number>>({}); // {studentId: amount}
  const [studentExtraFeeLabel, setStudentExtraFeeLabel] = useState<Record<string, string>>({}); // {studentId: label}
  const [showNewStudentBanner, setShowNewStudentBanner] = useState<Record<string, boolean>>({}); // {studentId: visible} default true
  // Ngày xin nghỉ giữa tháng: studentId → YYYY-MM-DD
  const [studentQuitDate, setStudentQuitDate] = useState<Record<string, string>>({});
  // Ẩn/hiện dòng "Nghỉ từ" khi chụp ảnh gửi PH
  const [showQuitDateSection, setShowQuitDateSection] = useState(false);

  const students = useSelector((state: any) => state.students.studentsList);
  const classes = useSelector((state: any) => state.classes.classesList);

  const schedulesList = useAppSelector((state: any) => state.schedules.schedulesList);
  const attendanceSessions = useSelector(
    (state: any) => state.attendanceSessions.attendanceSessionsList
  );
  const previousMonthAttendanceStudentRecords = useSelector(
    (state: any) => state.attendanceStudentRecords.attendanceStudentRecordsList
  );

  // Không cho người dùng thay đổi bằng UI — dùng tháng hiện tại làm mặc định
  const attendanceTo = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10); // ngày cuối tháng hiện tại
  // Tên tháng (dùng chung)
  const MONTH_NAMES = [
    'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
    'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
  ];
  const previousMonthName = (() => {
    const ref = new Date(attendanceTo);
    const prev = new Date(ref.getFullYear(), ref.getMonth() - 1, 1);
    return MONTH_NAMES[prev.getMonth()];
  })();

  const currentMonth = new Date().getMonth() + 1;
  const previousMonth = currentMonth == 1 ? 12 : new Date().getMonth(); //nếu hiện tại tháng 1 thì trả về Previous là 12, còn lại trả về đúng Previous luôn
  const currentYear = new Date().getFullYear();
  const yearToCountTuition = currentMonth == 1 ? currentYear - 1 : currentYear;

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      dispatch(classActions.getAll()),
      dispatch(studentActions.getAll()),
      dispatch(scheduleActions.getAll()),
      dispatch(attendanceActions.getAllAttendanceSessions()),
      dispatch(
        attendanceStudentRecordActions.getAttendanceStudentRecordByMonth(
          yearToCountTuition,
          previousMonth
        )
      ),
    ])
      .then(() => { setIsLoading(false); })
      .catch((err) => {
        console.error('Error loading data:', err);
        setIsLoading(false);
      });

    // Load bank accounts
    bankAccountService.getAll().then(setBankAccounts).catch(() => setBankAccounts([]));

    // Fetch confirmed sessions của tháng hiện tại từ DB
    classMonthlySessionService.getConfirmedByClass(currentMonth, currentYear)
      .then((res) => {
        const activeMap: Record<number, string[]> = {};
        Object.entries(res.data || {}).forEach(([cId, sessions]) => {
          activeMap[Number(cId)] = (sessions as any[]).map(s => s.sessionDate?.split('T')[0] ?? s.sessionDate);
        });
        setConfirmedSessionsMap(activeMap);
        setConfirmedSessionsLoaded(true);
      })
      .catch(() => { setConfirmedSessionsLoaded(true); });

    // Fetch đồng thời các buổi nghỉ lễ (isActive=false) để hiển thị gợi nhớ
    classMonthlySessionService.getAll({ month: currentMonth, year: currentYear })
      .then((res) => {
        const allSessions: any[] = res.data || [];
        const holidayMap: Record<number, string[]> = {};
        allSessions.filter(s => !s.isActive).forEach(s => {
          if (!holidayMap[s.classId]) holidayMap[s.classId] = [];
          const d = s.sessionDate?.split('T')[0] ?? s.sessionDate;
          const [y, m, day] = d.split('-').map(Number);
          holidayMap[s.classId].push(`${day}/${m}`);
        });
        setHolidaySessionsMap(holidayMap);
      })
      .catch(() => { });
  }, [dispatch]);

  // Auto calculate fee when selectedStudents changes
  useEffect(() => {
    if (selectedStudents.length === 0) {
      setAttendanceFee([]);
      return;
    }

    if (isLoading) return;

    // Calculate fee automatically
    const results = selectedStudents.map((studentId: string) => {
      const student = students.find((s: any) => s.id == studentId);
      const studentClass = classes.find((cls: any) => cls.id == student?.classId);
      const feePerSession = studentClass.sessionFee;

      const absences = previousMonthAttendanceStudentRecords.filter(
        (record: any) =>
          record.studentId === student?.id &&
          record.isPresent === false &&
          attendanceSessions.some(
            (session: any) =>
              session.id === record.sessionId &&
              new Date(session.date).getFullYear() === yearToCountTuition &&
              new Date(session.date).getMonth() + 1 === previousMonth
          )
      );

      const presences = previousMonthAttendanceStudentRecords.filter(
        (record: any) =>
          record.studentId === student?.id &&
          record.isPresent === true &&
          attendanceSessions.some(
            (session: any) =>
              session.id === record.sessionId &&
              new Date(session.date).getFullYear() === yearToCountTuition &&
              new Date(session.date).getMonth() + 1 === previousMonth
          )
      );

      const absenceDates = absences.map((absence: any) => {
        const session = attendanceSessions.find((s: any) => s.id == absence.sessionId);
        return session
          ? new Date(session.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }).replace('-', '/')
          : '';
      });

      const presenceDates = presences.map((presence: any) => {
        const session = attendanceSessions.find((s: any) => s.id == presence.sessionId);
        return session
          ? new Date(session.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }).replace('-', '/')
          : '';
      });

      const absencesLastMonth = absenceDates.length;
      const presencesLastMonth = presenceDates.length;

      // Khi học sinh xin nghỉ giữa tháng: dùng số buổi tính tới ngày đó
      const quitDate = studentQuitDate[String(student?.id)];
      const sessionsThisMonth = quitDate
        ? getSessionsUntilQuitDate(studentClass.id, student, quitDate).count
        : getSessionsThisMonth(studentClass.id, student);

      const currentMonthFee = sessionsThisMonth * feePerSession;
      const lastMonthAbsenceFee = absencesLastMonth * feePerSession;
      const lastMonthPresenceFee = presencesLastMonth * feePerSession;
      const totalFeeMinusPreviousAbsenceFee = currentMonthFee - lastMonthAbsenceFee;
      const totalFeeWithPreviousMonthFee = currentMonthFee + lastMonthPresenceFee;

      return {
        student,
        absencesLastMonth,
        presencesLastMonth,
        absenceDates,
        presenceDates,
        feePerSession,
        lastMonthAbsenceFee,
        lastMonthPresenceFee,
        currentMonthFee,
        totalFeeWithPreviousMonthFee,
        totalFeeMinusPreviousAbsenceFee,
      };
    });

    setAttendanceFee(results);
  }, [selectedStudents, studentExtraFee, studentIncludePrevMonth, studentQuitDate, isLoading, students, classes, previousMonthAttendanceStudentRecords, attendanceSessions, confirmedSessionsMap]);

  // New handlers for student list
  const handleStudentCheckbox = async (studentId: string) => {
    if (selectedStudents.includes(studentId)) {
      // Unchecking - remove this student only
      setSelectedStudents(prev => prev.filter(id => id !== studentId));
    } else {
      // Checking - add student + auto-select siblings
      try {
        const siblings = await studentService.getSiblings(Number(studentId));
        const siblingIds = siblings.map((s: any) => String(s.id));
        setSelectedStudents(prev => {
          const newIds = [studentId, ...siblingIds].filter(id => !prev.includes(id));
          return [...prev, ...newIds];
        });
      } catch (error) {
        console.error('Error loading siblings:', error);
        // Fallback: just add the student without siblings
        setSelectedStudents(prev => [...prev, studentId]);
      }
    }
  };

  const handleExtraFeeCheckbox = (studentId: string, checked: boolean) => {
    setStudentExtraFee(prev => ({
      ...prev,
      [studentId]: checked
    }));
  };

  const handlePrevMonthCheckbox = (studentId: string, checked: boolean) => {
    setStudentIncludePrevMonth(prev => ({
      ...prev,
      [studentId]: checked
    }));
  };

  const handleStudentExtraFeeAmountChange = (studentId: string, value: number) => {
    setStudentExtraFeeAmount(prev => ({
      ...prev,
      [studentId]: value
    }));
  };

  const handleStudentExtraFeeLabelChange = (studentId: string, value: string) => {
    setStudentExtraFeeLabel(prev => ({
      ...prev,
      [studentId]: value
    }));
  };

  const getStudentExtraFeeAmount = (studentId: string): number => {
    return studentExtraFeeAmount[studentId] || 110000; // Default 110k
  };

  const getStudentExtraFeeLabel = (studentId: string): string => {
    return studentExtraFeeLabel[studentId] || 'Photo tài liệu, in đề bài tập, in thẻ từ, ... (Thu 1 lần cả năm)';
  };

  const handleSelectAllStudents = (checked: boolean) => {
    if (checked) {
      const filtered = getFilteredStudents();
      setSelectedStudents(filtered.map((s: any) => String(s.id)));
    } else {
      setSelectedStudents([]);
    }
  };

  const getFilteredStudents = () => {
    return students.filter((student: any) => {
      // Filter by class
      if (filterClassId && String(student.classId) !== filterClassId) {
        return false;
      }
      // Filter by search text
      if (searchText && !student.name.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
      // Only show students with startDate
      if (!student.startDate) {
        return false;
      }
      return true;
    });
  };

  const countClassSessionsInThisMonth = ( // Hàm này đếm số buổi học dựa vào startDate và lịch học của lớp; nếu hsinh mới startDate trong tháng này, thì hàm sẽ tính số buổi học từ startDate đến cuối tháng thôi.
    classId: number,
    startDate: string,
    schedules: Schedule[]
  ): number => {
    const fromDate = new Date(new Date(startDate).toLocaleString('en-US', { timeZone: 'Asia/Ho_Chi_Minh' }));
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999); // Cho chắc chắn current <= endOfMonth sẽ bao phủ trọn ngày

    const classSchedules = schedules
      .filter(s => s.classId === classId)
      .map(s => s.dayOfWeek); // dayOfWeek là 2-8

    let count = 0;
    const current = new Date(fromDate);

    const convertToCustomDayOfWeek = (jsDay: number): number => {
      return jsDay === 0 ? 8 : jsDay + 1; // JS Sunday = 0 => 8, Monday = 1 => 2, ...
    };

    while (current <= endOfMonth) {
      const customDayOfWeek = convertToCustomDayOfWeek(current.getDay());

      if (classSchedules.includes(customDayOfWeek)) {
        count++;
      }

      current.setDate(current.getDate() + 1);
    }

    return count;
  };


  const isDateInCurrentMonth = (startDate: string | Date): boolean => {
    const date = new Date(startDate);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  };

  const isDateInPreviousMonth = (startDate: string | Date): boolean => {
    const date = new Date(startDate);
    return date.getMonth() + 1 === previousMonth && date.getFullYear() === yearToCountTuition;
  };

  const getNewStudentInfo = (student: any): { label: string; color: string; bg: string } | null => {
    if (!student.startDate) return null;
    if (isDateInCurrentMonth(student.startDate))
      return { label: '🌟 Mới tháng này', color: '#7c3aed', bg: '#f5f3ff' };
    if (isDateInPreviousMonth(student.startDate))
      return { label: '🌱 Mới tháng trước', color: '#047857', bg: '#ecfdf5' };
    return null;
  };

  const fmtStartDate = (startDate: string | Date): string =>
    new Date(startDate).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Trả về số buổi học từ confirmed sessions DB
  // Học sinh mới (startDate trong tháng): đếm sessions >= startDate trong confirmed list
  // Học sinh cũ: đếm toàn bộ confirmed sessions của lớp
  // Lớp chưa confirm: trả -1 (sentinel báo lỗi)
  const getSessionsThisMonth = (classId: string | number, student: any): number => {
    const cId = Number(classId);
    const sessionDates = confirmedSessionsMap[cId];

    if (sessionDates === undefined) {
      // Lớp chưa xác nhận buổi tháng này
      return -1;
    }

    if (student.startDate && isDateInCurrentMonth(student.startDate)) {
      // Học sinh mới: chỉ tính các buổi confirmed >= startDate
      const startStr = new Date(student.startDate).toISOString().split('T')[0];
      return sessionDates.filter(d => d >= startStr).length;
    }

    // Học sinh cũ: toàn bộ buổi đã confirmed
    return sessionDates.length;
  };

  /**
   * Tính số buổi học tính tới ngày xin nghỉ (inclusive).
   * Chỉ dùng confirmed sessions, lọc ≤ quitDate.
   * Phân tích thêm: danh sách ngày đã đi (dựa vào attended sessions từ DB attendance),
   * ngày nghỉ = confirmed session không có trong attendance.
   * Vì chưa có attendance DB ở đây, ta chỉ trả về danh sách ngày buổi học confirmed ≤ quitDate.
   */
  const getSessionsUntilQuitDate = (
    classId: string | number,
    student: any,
    quitDate: string
  ): { count: number; sessionDateList: string[] } => {
    const cId = Number(classId);
    const allSessions = confirmedSessionsMap[cId];
    if (!allSessions) return { count: 0, sessionDateList: [] };

    // Điểm bắt đầu: startDate nếu học sinh mới trong tháng
    const startStr = student.startDate && isDateInCurrentMonth(student.startDate)
      ? new Date(student.startDate).toISOString().split('T')[0]
      : null;

    const filtered = allSessions
      .filter(d => d <= quitDate)               // ≤ ngày nghỉ
      .filter(d => !startStr || d >= startStr); // ≥ startDate nếu học sinh mới

    return { count: filtered.length, sessionDateList: filtered };
  };

  /** Format YYYY-MM-DD → d/m */
  const fmtSessionDate = (d: string): string => {
    const [, m, day] = d.split('-');
    return `${Number(day)}/${Number(m)}`;
  };

  const handleCalculateFee = () => {
    const results = selectedStudents.map((studentId: string) => {
      const student = students.find((s: any) => s.id == studentId);
      const studentClass = classes.find((cls: any) => cls.id == student?.classId);
      const feePerSession = studentClass.sessionFee;

      // Lấy danh sách các ngày nghỉ và ngày đi của tháng trước
      // (sử dụng biến currentMonth/previousMonth/yearToCountTuition đã khai báo ở scope trên)

      const absences = previousMonthAttendanceStudentRecords.filter(
        (record: any) =>
          record.studentId === student?.id &&
          record.isPresent === false &&
          attendanceSessions.some(
            (session: any) =>
              session.id === record.sessionId &&
              new Date(session.date).getFullYear() === yearToCountTuition && // Kiểm tra năm
              new Date(session.date).getMonth() + 1 === previousMonth // Kiểm tra tháng
          )
      );

      const presences = previousMonthAttendanceStudentRecords.filter(
        (record: any) =>
          record.studentId === student?.id &&
          record.isPresent === true &&
          attendanceSessions.some(
            (session: any) =>
              session.id === record.sessionId &&
              new Date(session.date).getFullYear() === yearToCountTuition && // Kiểm tra năm
              new Date(session.date).getMonth() + 1 === previousMonth // Kiểm tra tháng
          )
      );

      // Danh sách ngày nghỉ (dạng ngày/tháng)
      const absenceDates = absences.map((absence: any) => {
        const session = attendanceSessions.find((s: any) => s.id == absence.sessionId);
        return session
          ? new Date(session.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }).replace('-', '/')
          : '';
      });

      // Danh sách ngày đi (dạng ngày/tháng)
      const presenceDates = presences.map((presence: any) => {
        const session = attendanceSessions.find((s: any) => s.id == presence.sessionId);
        return session
          ? new Date(session.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }).replace('-', '/')
          : '';
      });

      const absencesLastMonth = absenceDates.length;
      const presencesLastMonth = presenceDates.length;
      const currentMonthFee = getSessionsThisMonth(studentClass.id, student) * feePerSession;
      const lastMonthAbsenceFee = absencesLastMonth * feePerSession;
      const lastMonthPresenceFee = presencesLastMonth * feePerSession;
      const totalFeeMinusPreviousAbsenceFee = currentMonthFee - lastMonthAbsenceFee;
      const totalFeeWithPreviousMonthFee = currentMonthFee + lastMonthPresenceFee; // nếu tính học phí tháng này và cả tháng trước đó nữa.


      return {
        student,
        absencesLastMonth,
        presencesLastMonth,
        absenceDates, // <-- Thêm danh sách ngày nghỉ
        presenceDates, // <-- Thêm danh sách ngày đi
        feePerSession,
        lastMonthAbsenceFee,
        lastMonthPresenceFee,
        currentMonthFee, // học phí đi đủ cả tháng này (CHƯA TRỪ NGHỈ THÁNG TRƯỚC)
        totalFeeWithPreviousMonthFee, // là tổng của học phí đi đủ cả tháng này (và các buổi ĐI THÁNG TRƯỚC)
        totalFeeMinusPreviousAbsenceFee, // học phí đi đủ cả tháng này (sau khi Đã TRỪ NGHỈ THÁNG TRƯỚC)
      };
    });

    setAttendanceFee(results);
  };

  const getMonthName = (date: string) => {
    const month = new Date(date).getMonth();
    const months = [
      'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
      'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
    ];
    return months[month];
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('vi-VN'); // Định dạng số theo chuẩn Việt Nam
  };

  const getMonthList = (fromM: number, fromY: number, toM: number, toY: number) => {
    const list: { month: number; year: number }[] = [];
    let m = fromM, y = fromY;
    while (y < toY || (y === toY && m <= toM)) {
      list.push({ month: m, year: y });
      m++; if (m > 12) { m = 1; y++; }
    }
    return list;
  };

  const fetchMultiMonthSummary = async () => {
    if (selectedStudents.length === 0) return;
    setMultiMonthLoading(true);
    try {
      const monthList = getMonthList(summaryFromMonth, summaryFromYear, summaryToMonth, summaryToYear);
      const result: Record<number, any[]> = {};
      await Promise.all(
        selectedStudents.map(async (sId) => {
          const id = Number(sId);
          const records: any[] = [];
          await Promise.all(
            monthList.map(async ({ month, year }) => {
              // Nếu học sinh có ngày nghỉ và đây là tháng hiện tại → dùng HP đã tính trên table
              const hasQuit = !!studentQuitDate[sId];
              if (hasQuit && month === currentMonth && year === currentYear) {
                const feeEntry = attendanceFee.find((f: FeeResult) => String(f.student?.id) === sId);
                if (feeEntry) {
                  const hasExtraFee = studentExtraFee[sId] || false;
                  const extraAmt = hasExtraFee ? (studentExtraFeeAmount[sId] || 110000) : 0;
                  const totalFee = feeEntry.totalFeeMinusPreviousAbsenceFee + extraAmt;
                  records.push({ month, year, totalFee, _fromLive: true });
                }
                return;
              }
              try {
                const res = await tuitionDetailService.getAll({ month, year, studentId: id });
                const data = res.data || res;
                if (Array.isArray(data) && data.length > 0) records.push(data[0]);
                else if (data && !Array.isArray(data) && (data as any).month) records.push(data);
              } catch { /* no data */ }
            })
          );
          records.sort((a: any, b: any) => a.year !== b.year ? a.year - b.year : a.month - b.month);
          result[id] = records;
        })
      );
      setMultiMonthData(result);
    } finally {
      setMultiMonthLoading(false);
    }
  };

  const buildSummaryText = (): string => {
    if (selectedStudents.length === 0) return '';
    const monthList = getMonthList(summaryFromMonth, summaryFromYear, summaryToMonth, summaryToYear);
    const monthLabels = (() => {
      const groups = new Map<number, number[]>();
      monthList.forEach(({ month, year }) => {
        if (!groups.has(year)) groups.set(year, []);
        groups.get(year)!.push(month);
      });
      return Array.from(groups.entries())
        .map(([year, months]: [number, number[]]) => `${months.join(', ')}/${year}`)
        .join(', ');
    })();
    const studentNames = selectedStudents.map(sId => {
      const s = students.find((st: any) => st.id === Number(sId));
      return s?.name || sId;
    });
    const firstStudent = students.find((s: any) => s.id === Number(selectedStudents[0]));
    const cls = classes.find((c: any) => c.id === firstStudent?.classId);
    const subjectLabel = cls?.name?.includes('Toán') ? 'Toán' : 'Tiếng Anh';
    const isSingle = selectedStudents.length === 1;
    let text = `[Học phí ${subjectLabel} tháng ${monthLabels}]\n`;
    text += `${studentNames.join(', ')}\n`;
    let grandTotal = 0;

    // Helper: lấy HP tháng hiện tại từ live-calculated nếu học sinh đang nghỉ giữa tháng
    const getLiveFeeForCurrentMonth = (sId: string): number | null => {
      if (!studentQuitDate[sId]) return null;
      const feeEntry = attendanceFee.find((f: FeeResult) => String(f.student?.id) === sId);
      if (!feeEntry) return null;
      const hasExtraFee = studentExtraFee[sId] || false;
      const extraAmt = hasExtraFee ? (studentExtraFeeAmount[sId] || 110000) : 0;
      return feeEntry.totalFeeMinusPreviousAbsenceFee + extraAmt;
    };

    if (isSingle) {
      const sId = selectedStudents[0];
      const records = multiMonthData[Number(sId)] || [];
      monthList.forEach(({ month, year }) => {
        // Override tháng hiện tại bằng HP live nếu học sinh nghỉ giữa tháng
        if (month === currentMonth && year === currentYear) {
          const liveFee = getLiveFeeForCurrentMonth(sId);
          if (liveFee !== null) {
            grandTotal += liveFee;
            const [qy, qm, qd] = studentQuitDate[sId].split('-');
            const qDateLabel = `${Number(qd)}/${Number(qm)}`;
            text += `- Tháng ${month}/${year} ::: ${liveFee.toLocaleString('vi-VN')} (Tính tới ${qDateLabel})\n`;
            return;
          }
        }
        const rec = records.find((r: any) => r.month === month && r.year === year);
        if (rec) { const fee = Number(rec.totalFee); grandTotal += fee; text += `- Tháng ${month}/${year} ::: ${fee.toLocaleString('vi-VN')} (Chi tiết như ảnh HP T${month} ở trên)\n`; }
        else text += `- Tháng ${month}/${year} ::: ⚠️ Chưa có dữ liệu (vào Thống kê HP → Tự động tính tháng ${month}/${year} cho ${studentNames[0]})\n`;
      });
    } else {
      monthList.forEach(({ month, year }) => {
        let monthTotal = 0; const missing: string[] = [];
        selectedStudents.forEach(sId => {
          const s = students.find((st: any) => st.id === Number(sId));
          // Override tháng hiện tại nếu học sinh nghỉ giữa tháng
          if (month === currentMonth && year === currentYear) {
            const liveFee = getLiveFeeForCurrentMonth(sId);
            if (liveFee !== null) { monthTotal += liveFee; return; }
          }
          const rec = (multiMonthData[Number(sId)] || []).find((r: any) => r.month === month && r.year === year);
          if (rec) monthTotal += Number(rec.totalFee);
          else missing.push(s?.name || String(sId));
        });
        grandTotal += monthTotal;
        text += `- Tháng ${month}/${year} ::: ${monthTotal.toLocaleString('vi-VN')}${missing.length ? ' ⚠️ (chưa có HP: ' + missing.join(', ') + ')' : ''} (Chi tiết như ảnh HP T${month} ở trên)\n`;
      });
    }
    text += `🐬 Tổng: ${grandTotal.toLocaleString('vi-VN')}`;
    return text;
  };

  const handleCopySummary = () => {
    const text = buildSummaryText();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // ── Helpers: bank account of a student ─────────────────────
  const getBankAccountOfStudent = (sId: string): BankAccount | undefined => {
    const s = students.find((st: any) => st.id === Number(sId));
    if (!s?.bankAccountId) return undefined;
    return bankAccounts.find(b => b.id === Number(s.bankAccountId));
  };

  // Get unique bank accounts used by currently selected students
  const getSelectedStudentsBanks = (): { bankAccount: BankAccount; studentNames: string[] }[] => {
    const map = new Map<number, { bankAccount: BankAccount; studentNames: string[] }>();
    for (const sId of selectedStudents) {
      const s = students.find((st: any) => st.id === Number(sId));
      const bank = getBankAccountOfStudent(sId);
      if (bank) {
        if (!map.has(bank.id)) map.set(bank.id, { bankAccount: bank, studentNames: [] });
        map.get(bank.id)!.studentNames.push(s?.name || sId);
      }
    }
    return Array.from(map.values());
  };

  const buildBankText = (months?: number[], overrideBankAccountId?: number): string => {
    if (selectedStudents.length === 0) return '';
    const studentNames = selectedStudents.map(sId => {
      const s = students.find((st: any) => st.id === Number(sId));
      return s?.name || sId;
    });
    const monthLabel = months && months.length > 0
      ? months.join(', ')
      : String(currentMonth);

    // Resolve bank account: override (from modal) > first selected student's bank
    let bank: BankAccount | undefined;
    if (overrideBankAccountId) {
      bank = bankAccounts.find(b => b.id === overrideBankAccountId);
    } else {
      bank = getBankAccountOfStudent(selectedStudents[0]);
    }

    const stkLine = bank
      ? `STK: ${bank.accountNumber}  (${bank.accountHolder} - ${bank.bankName})`
      : 'STK: (Chưa có tài khoản ngân hàng)';

    return `Thầy gửi học phí tháng ${monthLabel} của ${studentNames.join(', ')} ạ!\n${stkLine}`;
  };

  const handleCopyBankText = (position: 'top' | 'bottom', months?: number[]) => {
    const banks = getSelectedStudentsBanks();

    // Multiple different banks → open picker modal
    if (banks.length > 1) {
      setStkModalPosition(position);
      setStkModalMonths(months);
      setStkModalChosenId(banks[0].bankAccount.id);
      setStkModalOpen(true);
      return;
    }

    // Single bank (or none) → copy directly
    const text = buildBankText(months);
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopyBankSuccess(position);
      setTimeout(() => setCopyBankSuccess(null), 2000);
    });
  };

  const handleStkModalCopy = () => {
    if (!stkModalChosenId) return;
    const text = buildBankText(stkModalMonths, Number(stkModalChosenId));
    navigator.clipboard.writeText(text).then(() => {
      setStkModalOpen(false);
      setCopyBankSuccess(stkModalPosition);
      setTimeout(() => setCopyBankSuccess(null), 2000);
    });
  };

  return (
    <>
      <TuitionContainer>
        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
          Tính học phí mới - {getMonthName(attendanceTo)}
        </h2>
        {isLoading ? (
          <p>Đang tải dữ liệu...</p>
        ) : (
          <div style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 150px)' }}>
            {/* Left Panel - Student List */}
            <div style={{
              flex: '0 0 420px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '15px',
              backgroundColor: '#f9f9f9',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h5 style={{ margin: 0 }}>Danh sách học sinh</h5>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setSelectedStudents([]);
                    setFilterClassId('');
                    setSearchText('');
                  }}
                  style={{ fontSize: '12px', padding: '4px 12px' }}
                >
                  Bỏ chọn hết
                </Button>
              </div>

              {/* Search and Filter */}
              <div style={{ marginBottom: '15px' }}>
                <Input
                  type="text"
                  placeholder="🔍 Tìm theo tên học sinh..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ marginBottom: '10px' }}
                />
                <Input
                  type="select"
                  value={filterClassId}
                  onChange={(e) => setFilterClassId(e.target.value)}
                >
                  <option value="">Tất cả lớp</option>
                  {[...classes]
                    .sort((a: any, b: any) => a.name.localeCompare(b.name))
                    .map((cls: any) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                </Input>
              </div>

              {/* Select All Checkbox */}
              <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #ddd' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={getFilteredStudents().length > 0 && getFilteredStudents().every((s: any) => selectedStudents.includes(String(s.id)))}
                    onChange={(e) => handleSelectAllStudents(e.target.checked)}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <strong>Chọn tất cả ({getFilteredStudents().length})</strong>
                </label>
              </div>

              {/* Student List */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f9f9f9', zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '40px', padding: '6px 4px', textAlign: 'center' }}></th>
                      <th style={{ maxWidth: '120px', padding: '6px 4px', textAlign: 'left' }}>Tên</th>
                      <th style={{ width: '60px', maxWidth: '60px', padding: '6px 4px', textAlign: 'left', fontSize: '11px', wordWrap: 'break-word' }}>Lớp</th>
                      <th style={{ padding: '6px 4px', textAlign: 'center', fontSize: '11px' }} title="Thu phí phụ">Phí<br />phụ</th>
                      <th style={{ padding: '6px 4px', textAlign: 'center', fontSize: '11px' }} title="Tính cả tháng trước">Tháng<br />trước</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredStudents().map((student: any) => {
                      const studentClass = classes.find((c: any) => c.id === student.classId);
                      const isSelected = selectedStudents.includes(String(student.id));
                      return (
                        <tr
                          key={student.id}
                          style={{
                            borderBottom: '1px solid #eee',
                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                          }}
                        >
                          <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleStudentCheckbox(String(student.id))}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                          <td
                            style={{ maxWidth: '120px', padding: '6px 4px', cursor: 'pointer', wordWrap: 'break-word', whiteSpace: 'normal' }}
                            onClick={() => handleStudentCheckbox(String(student.id))}
                          >
                            <div>{student.name}</div>
                            {(() => {
                              const info = getNewStudentInfo(student);
                              if (!info) return null;
                              return (
                                <span style={{
                                  display: 'inline-block', fontSize: 10, fontWeight: 700,
                                  color: info.color, background: info.bg,
                                  border: `1px solid ${info.color}44`,
                                  borderRadius: 8, padding: '1px 6px', marginTop: 2,
                                }}>
                                  {info.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ width: '60px', maxWidth: '60px', padding: '6px 4px', fontSize: '11px', color: '#666', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                            {studentClass?.name || 'N/A'}
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={studentExtraFee[String(student.id)] || false}
                              onChange={(e) => handleExtraFeeCheckbox(String(student.id), e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              disabled={!isSelected}
                            />
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={studentIncludePrevMonth[String(student.id)] || false}
                              onChange={(e) => handlePrevMonthCheckbox(String(student.id), e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              disabled={!isSelected}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {getFilteredStudents().length === 0 && (
                  <p style={{ textAlign: 'center', color: '#999', marginTop: '20px' }}>
                    Không tìm thấy học sinh
                  </p>
                )}
              </div>

              {/* Selected Count */}
              <div style={{
                marginTop: '10px',
                paddingTop: '10px',
                borderTop: '1px solid #ddd',
              }}>
                <div style={{ fontWeight: 'bold', color: '#007bff', marginBottom: '8px' }}>
                  Đã chọn: {selectedStudents.length} học sinh
                </div>
                {selectedStudents.length > 0 && (
                  <div style={{
                    maxHeight: '120px',
                    overflowY: 'auto',
                    fontSize: '12px',
                    backgroundColor: '#f8f9fa',
                    padding: '8px',
                    borderRadius: '4px'
                  }}>
                    {selectedStudents.map((studentId) => {
                      const student = students.find((s: any) => s.id === Number(studentId));
                      const studentClass = classes.find((c: any) => c.id === student?.classId);
                      return (
                        <div
                          key={studentId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '4px 0',
                            borderBottom: '1px solid #e9ecef'
                          }}
                        >
                          <span style={{ flex: 1 }}>
                            {student?.name} <span style={{ color: '#666', fontSize: '11px' }}>({studentClass?.name})</span>
                          </span>
                          <button
                            onClick={() => handleStudentCheckbox(studentId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              fontSize: '16px',
                              padding: '0 4px',
                              lineHeight: '1'
                            }}
                            title="Bỏ chọn"
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Calculation */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 10px' }}>

              {/* Toggle ẩn/hiện Nghỉ từ */}
              {attendanceFee.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                  <button
                    onClick={() => setShowQuitDateSection(v => !v)}
                    style={{
                      background: showQuitDateSection ? '#fff3e0' : '#f5f5f5',
                      border: `1.5px solid ${showQuitDateSection ? '#ff9800' : '#bdbdbd'}`,
                      borderRadius: 8, padding: '3px 12px',
                      fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      color: showQuitDateSection ? '#e65100' : '#757575',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}
                    title={showQuitDateSection ? 'Ẩn dòng Nghỉ từ (để chụp ảnh gửi PH)' : 'Hiện lại dòng Nghỉ từ'}
                  >
                    {showQuitDateSection ? '🙈 Ẩn "Nghỉ từ"' : '👁️ Hiện "Nghỉ từ"'}
                  </button>
                </div>
              )}

              {attendanceFee.length > 0 && (
                attendanceFee.map((fee, index) => {
                  const studentStartDate = fee.student.startDate;
                  const studentId = String(fee.student.id);
                  const studentClass = classes.find(
                    (cls: any) => cls.id === fee.student?.classId
                  );
                  const includePrevMonth = studentIncludePrevMonth[studentId] || false;
                  const hasExtraFee = studentExtraFee[studentId] || false;

                  return studentStartDate ?
                    (
                      <React.Fragment key={index}>
                        {/* Cảnh báo lớp chưa xác nhận buổi học */}
                        {(() => {
                          const sessions = getSessionsThisMonth(studentClass?.id, fee.student);
                          if (sessions !== -1) return null;
                          return (
                            <div style={{
                              background: '#fff3e0', border: '1.5px solid #ff9800',
                              borderRadius: 10, padding: '12px 16px', marginBottom: 12,
                              display: 'flex', gap: 12, alignItems: 'flex-start',
                            }}>
                              <span style={{ fontSize: 22 }}>⚠️</span>
                              <div>
                                <div style={{ fontWeight: 700, color: '#e65100', fontSize: 13 }}>
                                  Lớp <strong>{studentClass?.name}</strong> chưa xác nhận buổi học tháng {currentMonth}/{currentYear}
                                </div>
                                <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
                                  Vào màn hình <strong>"Lịch học"</strong>, chọn tháng {currentMonth}/{currentYear} và ấn <strong>"✅ Xác nhận tháng"</strong> cho lớp {studentClass?.name} trước khi tính học phí.
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        {/* Chỉ hiện bảng học phí nếu lớp đã xác nhận buổi tháng */}
                        {getSessionsThisMonth(studentClass?.id, fee.student) !== -1 && (
                          <>
                            {/* ── Nghỉ giữa tháng: input ngày bên ngoài table ── */}
                            {showQuitDateSection && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 10px', background: studentQuitDate[studentId] ? '#fff3e0' : '#f5f5f5', borderRadius: 8, border: studentQuitDate[studentId] ? '1.5px solid #ff9800' : '1px solid #e0e0e0' }}>
                                <span style={{ fontSize: '0.82rem', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>🗓️ Nghỉ từ:</span>
                                <input
                                  type="date"
                                  value={studentQuitDate[studentId] || ''}
                                  min={`${currentYear}-${String(currentMonth).padStart(2, '0')}-01`}
                                  max={`${currentYear}-${String(currentMonth).padStart(2, '0')}-${new Date(currentYear, currentMonth, 0).getDate()}`}
                                  onChange={e => setStudentQuitDate(prev => ({ ...prev, [studentId]: e.target.value }))}
                                  style={{ fontSize: '0.85rem', padding: '3px 8px', borderRadius: 4, border: '1.5px solid #ff9800', maxWidth: 160 }}
                                />
                                {studentQuitDate[studentId] ? (
                                  <>
                                    <span style={{ fontSize: '0.8rem', color: '#e65100', fontWeight: 600 }}>
                                      → Tính {getSessionsUntilQuitDate(studentClass.id, fee.student, studentQuitDate[studentId]).count} buổi
                                    </span>
                                    <button
                                      onClick={() => setStudentQuitDate(prev => { const n = { ...prev }; delete n[studentId]; return n; })}
                                      style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '0.8rem', padding: '0 4px', fontWeight: 600 }}
                                      title="Xóa ngày nghỉ"
                                    >
                                      ✕ Bỏ
                                    </button>
                                  </>
                                ) : (
                                  <span style={{ fontSize: '0.78rem', color: '#999', fontStyle: 'italic' }}>Chưa chọn — mặc định tính full tháng</span>
                                )}
                              </div>
                            )}
                            <StyledTable bordered>
                              <thead>
                                <tr>
                                  <th>
                                    {/* New student notice — inline in header, with toggle */}
                                    {(() => {
                                      const info = getNewStudentInfo(fee.student);
                                      if (!info) return null;
                                      const isThisMonth = isDateInCurrentMonth(fee.student.startDate);
                                      const sid = String(fee.student.id);
                                      const isVisible = showNewStudentBanner[sid] !== false;
                                      return (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 8 }}>
                                          <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                            background: info.bg,
                                            border: `1px solid ${info.color}66`,
                                            borderRadius: 8, padding: '1px 8px',
                                            fontSize: 12, fontWeight: 700, color: info.color,
                                          }}>
                                            {isVisible && (
                                              <span>{isThisMonth ? 'Học sinh mới tháng này' : 'Học sinh mới tháng trước'}</span>
                                            )}
                                            {isVisible && (
                                              <span style={{ fontWeight: 400, color: '#555', fontSize: 11 }}>
                                                {'– Bắt đầu: '}<strong>{fmtStartDate(fee.student.startDate)}</strong>
                                              </span>
                                            )}
                                            <button
                                              onClick={() => setShowNewStudentBanner(prev => ({ ...prev, [sid]: !isVisible }))}
                                              style={{
                                                background: 'none', border: 'none',
                                                cursor: 'pointer', fontSize: 11, color: info.color,
                                                padding: '0 2px', lineHeight: 1, opacity: 0.75,
                                              }}
                                              title={isVisible ? 'Ẩn thông tin' : 'Hiện thông tin'}
                                            >
                                              {isVisible ? '🙈 Ẩn' : '🌱'}
                                            </button>
                                          </span>
                                        </span>
                                      );
                                    })()}
                                    Nội dung: <span style={{ color: 'blue', fontWeight: 'bold' }}>
                                      HP {studentClass.name.includes('Toán') ? 'Toán' : 'Tiếng Anh'} {includePrevMonth && !isDateInCurrentMonth(fee.student.startDate) ? previousMonthName + ' và ' : ''} {getMonthName(attendanceTo)}
                                    </span>
                                    <br />
                                    <span style={{ color: 'blue', fontWeight: 'bold' }}>
                                      {fee.student ? fee.student.name : ""}
                                    </span>
                                  </th>
                                  <th>Số buổi</th>
                                  <th>Học phí 1 buổi</th>
                                  <th>Số tiền</th>
                                </tr>
                              </thead>
                              <tbody>
                                {includePrevMonth && !isDateInCurrentMonth(fee.student.startDate) ? (
                                  <>
                                    <tr>
                                      <td>Số buổi <strong> đã đi học </strong>tháng trước ({previousMonthName})</td>
                                      <td>{fee.presencesLastMonth}
                                        <br />
                                        {fee.presenceDates.length > 0 && (
                                          <>
                                            <small>(Đi ngày {fee.presenceDates.join('; ')});</small>
                                            {fee.absenceDates.length > 0 ? <><br /><small>(Nghỉ ngày {fee.absenceDates.join('; ')})</small></> : ''}
                                          </>
                                        )}
                                      </td>
                                      <td>{formatCurrency(fee.feePerSession)}</td>
                                      <td>
                                        {fee.presencesLastMonth} x {formatCurrency(fee.feePerSession)} = {formatCurrency(fee.lastMonthPresenceFee)}
                                      </td>
                                    </tr>
                                  </>
                                ) : (
                                  <>
                                    {!isDateInCurrentMonth(fee.student.startDate) &&
                                      <tr>
                                        <td>Số buổi <strong>nghỉ tháng trước</strong> ({previousMonthName})</td>
                                        <td>{fee.absencesLastMonth}
                                          <br />
                                          {fee.absenceDates.length > 0 && (
                                            <small>(Nghỉ {fee.absenceDates.join('; ')})</small>
                                          )}
                                        </td>
                                        <td>{formatCurrency(fee.feePerSession)}</td>
                                        <td>
                                          {fee.absencesLastMonth} x {formatCurrency(fee.feePerSession)} = {formatCurrency(fee.lastMonthAbsenceFee)}
                                        </td>
                                      </tr>
                                    }
                                  </>
                                )}
                                <tr>
                                  <td>
                                    Số buổi {getMonthName(attendanceTo)}
                                  </td>
                                  <td>
                                    {(() => {
                                      const qDate = studentQuitDate[studentId];
                                      if (qDate) {
                                        const { count, sessionDateList } = getSessionsUntilQuitDate(studentClass.id, fee.student, qDate);
                                        return (
                                          <>
                                            <span style={{ fontWeight: 700, color: '#1565c0' }}>{count} buổi</span>
                                            <span style={{ color: '#e65100', fontSize: '0.78rem', marginLeft: 4 }}>
                                              (tới ngày {new Date(qDate + 'T00:00:00').toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })})
                                            </span>
                                            {sessionDateList.length > 0 && (
                                              <div style={{ fontSize: '0.78rem', color: '#2e7d32', marginTop: 3 }}>
                                                Các buổi: {sessionDateList.map(fmtSessionDate).join(', ')}
                                              </div>
                                            )}
                                            {(holidaySessionsMap[studentClass?.id] || []).length > 0 && (
                                              <div style={{ color: '#e65100', fontSize: '0.78rem', marginTop: 2 }}>
                                                (Đã trừ nghỉ ngày {(holidaySessionsMap[studentClass.id] || []).join('; ')})
                                              </div>
                                            )}
                                          </>
                                        );
                                      }
                                      return (
                                        <>
                                          {getSessionsThisMonth(studentClass.id, fee.student)}{' '}
                                          {isDateInCurrentMonth(fee.student.startDate) ? '(Bắt đầu ' + new Date(fee.student.startDate).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }).replace('-', '/') + ')' : ''}
                                          {(holidaySessionsMap[studentClass?.id] || []).length > 0 && (
                                            <span style={{ color: '#e65100', fontSize: '0.8rem', display: 'block', marginTop: 2 }}>
                                              (Đã trừ nghỉ ngày {(holidaySessionsMap[studentClass.id] || []).join('; ')})
                                            </span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </td>
                                  <td>{formatCurrency(fee.feePerSession)}</td>
                                  <td>
                                    {(() => {
                                      const qDate = studentQuitDate[studentId];
                                      const sessions = qDate
                                        ? getSessionsUntilQuitDate(studentClass.id, fee.student, qDate).count
                                        : getSessionsThisMonth(studentClass.id, fee.student);
                                      return `${sessions} x ${formatCurrency(fee.feePerSession)} = ${formatCurrency(sessions * fee.feePerSession)}`;
                                    })()}
                                  </td>
                                </tr>
                                {hasExtraFee && (
                                  <tr>
                                    <td colSpan={1}>
                                      <input
                                        type="text"
                                        value={getStudentExtraFeeLabel(studentId)}
                                        onChange={(e) => handleStudentExtraFeeLabelChange(studentId, e.target.value)}
                                        style={{ width: '100%', backgroundColor: 'inherit', border: 'none', outline: 'none', padding: 0, margin: 0, fontSize: 'inherit' }}
                                      />
                                    </td>
                                    <td colSpan={1}></td>
                                    <td colSpan={1}></td>
                                    <td colSpan={1}>
                                      <input
                                        type="text"
                                        value={getStudentExtraFeeAmount(studentId).toLocaleString('vi-VN')}
                                        onChange={(e) => {
                                          let value = e.target.value.replace(/\./g, '');
                                          value = value.replace(/\D/g, '');
                                          handleStudentExtraFeeAmountChange(studentId, Number(value));
                                        }}
                                        style={{ width: '100px', textAlign: 'right', backgroundColor: 'inherit', border: 'none', outline: 'none' }}
                                      />
                                    </td>
                                  </tr>
                                )}
                                {selectedStudents.length === 1 && (<tr>
                                  {includePrevMonth ? (
                                    <>
                                      <td colSpan={3} style={{ textAlign: 'right', color: 'red', fontWeight: 'bold', fontSize: '1.1rem', padding: '10px', verticalAlign: 'middle' }}>
                                        TỔNG HỌC PHÍ: {formatCurrency(hasExtraFee ? getStudentExtraFeeAmount(studentId) + fee.totalFeeWithPreviousMonthFee : fee.totalFeeWithPreviousMonthFee)}
                                      </td>
                                      <td style={{ color: '#1a1a1a', fontSize: '0.88rem', verticalAlign: 'middle' }}>
                                        = {formatCurrency(fee.currentMonthFee)} + {formatCurrency(fee.lastMonthPresenceFee)} {hasExtraFee ? '+ ' + getStudentExtraFeeAmount(studentId).toLocaleString('vi-VN') : ''}
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td colSpan={3} style={{ textAlign: 'right', color: 'red', fontWeight: 'bold', fontSize: '1.1rem', padding: '10px', verticalAlign: 'middle' }}>
                                        TỔNG HỌC PHÍ: {formatCurrency(hasExtraFee ? getStudentExtraFeeAmount(studentId) + fee.totalFeeMinusPreviousAbsenceFee : fee.totalFeeMinusPreviousAbsenceFee)}
                                      </td>
                                      <td style={{ color: '#1a1a1a', fontSize: '0.88rem', verticalAlign: 'middle' }}>
                                        = {formatCurrency(fee.currentMonthFee)} {hasExtraFee ? '+ ' + getStudentExtraFeeAmount(studentId).toLocaleString('vi-VN') : ''} {isDateInCurrentMonth(fee.student.startDate) ? '' : '- ' + formatCurrency(fee.lastMonthAbsenceFee)}
                                      </td>
                                    </>
                                  )}
                                </tr>)}
                              </tbody>
                            </StyledTable>
                            {/* Hiển thị đoạn văn bản nếu chỉ chọn 1 học sinh */}
                            {selectedStudents.length === 1 && (
                              <>
                                <span style={{ display: 'block', color: 'red' }}>
                                  <i>*Học phí thu chung đầu tháng, các buổi nghỉ tháng này sẽ được trừ vào học phí đầu tháng sau.</i>
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </React.Fragment>
                    ) : <p style={{ color: 'red', fontWeight: 'bold', verticalAlign: 'top' }}>Học Sinh Chưa Có startDate</p>;
                })
              )}

              {/* Bảng tổng kết khi chọn nhiều học sinh */}
              {selectedStudents.length > 1 && attendanceFee.length > 0 && (
                <>
                  <Table bordered style={{ marginTop: '20px' }}>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: 'right', color: 'red', fontWeight: 'bold', fontSize: '1.1rem', padding: '10px', verticalAlign: 'middle' }}>
                          TỔNG HỌC PHÍ: {formatCurrency(
                            attendanceFee.reduce((acc, fee) => {
                              const studentId = String(fee.student.id);
                              const includePrevMonth = studentIncludePrevMonth[studentId] || false;
                              const hasExtraFee = studentExtraFee[studentId] || false;
                              const extraFeeAmount = hasExtraFee ? getStudentExtraFeeAmount(studentId) : 0;
                              return acc + fee.currentMonthFee + extraFeeAmount + (includePrevMonth ? fee.lastMonthPresenceFee : (0 - fee.lastMonthAbsenceFee));
                            }, 0)
                          )}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#1a1a1a', fontSize: '0.88rem' }}>
                          {attendanceFee.map((fee, index) => {
                            const studentId = String(fee.student.id);
                            const includePrevMonth = studentIncludePrevMonth[studentId] || false;
                            const hasExtraFee = studentExtraFee[studentId] || false;
                            const extraFeeAmount = getStudentExtraFeeAmount(studentId);
                            return (
                              <div key={index}>
                                {fee.student.name}: {formatCurrency(fee.currentMonthFee)} {includePrevMonth && !isDateInCurrentMonth(fee.student.startDate) ? '+ ' + formatCurrency(fee.lastMonthPresenceFee) : isDateInCurrentMonth(fee.student.startDate) ? '' : '- ' + formatCurrency(fee.lastMonthAbsenceFee)} {hasExtraFee ? '+ ' + extraFeeAmount.toLocaleString('vi-VN') : ''} = {formatCurrency(includePrevMonth ? fee.totalFeeWithPreviousMonthFee + (hasExtraFee ? extraFeeAmount : 0) : (fee.totalFeeMinusPreviousAbsenceFee + (hasExtraFee ? extraFeeAmount : 0)))}
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </>
              )}


              {/* Hiển thị đoạn văn bản khi chọn nhiều học sinh */}
              {selectedStudents.length > 1 && attendanceFee.length > 0 && (
                <>
                  {/* <span style={{ display: 'block' }}>
                <i>*Phụ huynh có thể chuyển khoản (Và gửi ảnh chuyển khoản):</i>
              </span>
              <span style={{ display: 'block' }}>
                <i>     STK: 02047318001 (DUONG KHAC TUNG - Ngân hàng Tiên Phong Bank - TPBank)</i>
              </span> */}
                  <span style={{ display: 'block', color: 'red' }}>
                    <i>*Học phí thu chung đầu tháng, các buổi nghỉ tháng này sẽ được trừ vào học phí đầu tháng sau.</i>
                  </span>
                </>
              )}

              {/* Nút copy tin nhắn gửi học phí */}
              {selectedStudents.length > 0 && attendanceFee.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, marginBottom: 4 }}>
                  <button
                    onClick={() => handleCopyBankText('top')}
                    style={{
                      background: copyBankSuccess === 'top' ? '#5b21b6' : '#7c3aed',
                      color: '#fff', border: 'none', borderRadius: 6,
                      padding: '6px 16px', fontSize: '0.85rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                    title="Copy tin nhắn gửi học phí kèm STK"
                  >
                    {copyBankSuccess === 'top' ? '✅ Đã copy!' : '🏦 Copy tin nhắn HP + STK'}
                  </button>
                </div>
              )}

              {/* ── Tổng học phí nhiều tháng ── */}
              {selectedStudents.length > 0 && (
                <div style={{
                  marginTop: 24, border: '1.5px solid #1976d2', borderRadius: 10,
                  padding: '16px 18px', background: '#f0f7ff',
                }}>
                  <div style={{ fontWeight: 700, color: '#1565c0', marginBottom: 12, fontSize: '0.95rem' }}>
                    📋 Tổng hợp học phí nhiều tháng
                  </div>
                  {/* Range picker */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                    <span style={{ fontSize: '0.85rem', color: '#555' }}>Từ tháng:</span>
                    <select
                      value={`${summaryFromMonth}-${summaryFromYear}`}
                      onChange={e => {
                        const [m, y] = e.target.value.split('-').map(Number);
                        setSummaryFromMonth(m); setSummaryFromYear(y);
                      }}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.85rem' }}
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const d = new Date(); d.setMonth(d.getMonth() - i);
                        const m = d.getMonth() + 1, y = d.getFullYear();
                        return <option key={i} value={`${m}-${y}`}>T{m}/{y}</option>;
                      })}
                    </select>
                    <span style={{ fontSize: '0.85rem', color: '#555' }}>→ Đến tháng:</span>
                    <select
                      value={`${summaryToMonth}-${summaryToYear}`}
                      onChange={e => {
                        const [m, y] = e.target.value.split('-').map(Number);
                        setSummaryToMonth(m); setSummaryToYear(y);
                      }}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.85rem' }}
                    >
                      {Array.from({ length: 12 }, (_, i) => {
                        const d = new Date(); d.setMonth(d.getMonth() - i);
                        const m = d.getMonth() + 1, y = d.getFullYear();
                        return <option key={i} value={`${m}-${y}`}>T{m}/{y}</option>;
                      })}
                    </select>
                    <button
                      onClick={fetchMultiMonthSummary}
                      disabled={multiMonthLoading}
                      style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                      {multiMonthLoading ? '⏳ Đang tải...' : '🔄 Tải HP các tháng'}
                    </button>
                  </div>
                  {/* Text summary */}
                  {Object.keys(multiMonthData).length > 0 && (
                    <>
                      <textarea
                        readOnly
                        value={buildSummaryText()}
                        rows={Math.min(buildSummaryText().split('\n').length + 1, 15)}
                        style={{
                          width: '100%', fontFamily: 'monospace', fontSize: '0.88rem',
                          border: '1px solid #90caf9', borderRadius: 6, padding: '10px 12px',
                          background: '#ffffff', color: '#1a1a1a', resize: 'vertical', lineHeight: 1.6,
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                        <button
                          onClick={handleCopySummary}
                          style={{
                            background: copySuccess ? '#2e7d32' : '#43a047', color: '#fff',
                            border: 'none', borderRadius: 6, padding: '6px 18px',
                            fontSize: '0.88rem', cursor: 'pointer',
                          }}
                        >
                          {copySuccess ? '✅ Đã copy!' : '📋 Copy text'}
                        </button>
                        <button
                          onClick={() => {
                            const months = getMonthList(summaryFromMonth, summaryFromYear, summaryToMonth, summaryToYear).map(({ month }) => month);
                            handleCopyBankText('bottom', months);
                          }}
                          style={{
                            background: copyBankSuccess === 'bottom' ? '#5b21b6' : '#7c3aed',
                            color: '#fff', border: 'none', borderRadius: 6,
                            padding: '6px 16px', fontSize: '0.88rem', cursor: 'pointer',
                          }}
                          title="Copy tin nhắn gửi học phí kèm STK"
                        >
                          {copyBankSuccess === 'bottom' ? '✅ Đã copy!' : '🏦 Copy tin nhắn HP + STK'}
                        </button>
                      </div>
                    </>
                  )}
                  {Object.keys(multiMonthData).length === 0 && (
                    <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>
                      Chọn khoảng tháng và ấn "Tải HP các tháng" để xem tổng hợp.
                    </p>
                  )}
                </div>
              )}

            </div>
          </div>
        )}
      </TuitionContainer>

      {/* ── STK Picker Modal: khi học sinh dùng nhiều ngân hàng khác nhau ── */}
      <Modal isOpen={stkModalOpen} toggle={() => setStkModalOpen(false)} centered>
        <ModalHeader
          toggle={() => setStkModalOpen(false)}
          style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff' }}
        >
          🏦 Chọn tài khoản ngân hàng để copy
        </ModalHeader>
        <ModalBody>
          <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: 12 }}>
            Các học sinh đang được tính học phí dùng <strong>nhiều tài khoản ngân hàng khác nhau</strong>.
            Hãy chọn tài khoản bạn muốn dùng trong tin nhắn:
          </p>

          {/* Chi tiết từng ngân hàng và học sinh dùng */}
          <div style={{ marginBottom: 16 }}>
            {getSelectedStudentsBanks().map(({ bankAccount, studentNames }) => (
              <div key={bankAccount.id} style={{
                background: stkModalChosenId === bankAccount.id ? '#ede9fe' : '#f8fafc',
                border: `2px solid ${stkModalChosenId === bankAccount.id ? '#7c3aed' : '#e2e8f0'}`,
                borderRadius: 10, padding: '10px 14px', marginBottom: 8,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
                onClick={() => setStkModalChosenId(bankAccount.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    checked={stkModalChosenId === bankAccount.id}
                    onChange={() => setStkModalChosenId(bankAccount.id)}
                    style={{ accentColor: '#7c3aed' }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, color: '#4338ca' }}>
                      {bankAccount.accountNumber} — {bankAccount.accountHolder}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#64748b' }}>{bankAccount.bankName}</div>
                    <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 2 }}>
                      👤 {studentNames.join(', ')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Preview nội dung sẽ copy */}
          {stkModalChosenId && (
            <div style={{
              background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem',
              fontFamily: 'monospace', whiteSpace: 'pre-line', color: '#1e40af',
            }}>
              {buildBankText(stkModalMonths, Number(stkModalChosenId))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setStkModalOpen(false)}>Huỷ</Button>
          <button
            onClick={handleStkModalCopy}
            disabled={!stkModalChosenId}
            style={{
              background: stkModalChosenId ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : '#94a3b8',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 22px', fontWeight: 700, cursor: stkModalChosenId ? 'pointer' : 'not-allowed',
            }}
          >
            📋 Copy tin nhắn
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TuitionManageNew;
